import { useState } from "react";
import { Loader2 } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";
import { Field, formatDateTime } from "@/components/admin/ui";
import { inputClass, labelClass, saveBtnClass } from "./orgFormStyles";
import OrgModal from "./OrgModal";

const ASSIGNABLE_ROLES = ["org_admin", "billing_admin", "team_lead", "user"];

const cancelBtnClass = "rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10";
const errorClass = "rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400";

export function InviteUserModal({ onClose, onSubmit, saving, error }) {
  const [form, setForm] = useState({ name: "", email: "", role: "user" });
  return (
    <OrgModal title="Invite User" subtitle="Send a new organization user an activation link." onClose={onClose} testId="org-invite-user-modal">
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Full Name</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="org-invite-name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="org-invite-email" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Role</label>
          <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="org-invite-role" className={inputClass}>
            {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </div>
        {error && <p className={errorClass} data-testid="org-invite-error">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => onSubmit(form)} disabled={saving || !form.email} data-testid="org-invite-submit-btn" className={saveBtnClass}>
            {saving && <Loader2 size={14} className="animate-spin" />} {saving ? "Sending…" : "Send Invitation"}
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-invite-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}

export function EditUserModal({ row, onClose, onSubmit, saving, error }) {
  const [form, setForm] = useState({ name: row.name || "", email: row.email || "" });
  return (
    <OrgModal title="Edit User" subtitle={row.email} onClose={onClose} testId="org-edit-user-modal">
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Name</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="org-edit-user-name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="org-edit-user-email" className={inputClass} />
        </div>
        {error && <p className={errorClass} data-testid="org-edit-user-error">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => onSubmit(form)} disabled={saving} data-testid="org-edit-user-submit-btn" className={saveBtnClass}>
            {saving && <Loader2 size={14} className="animate-spin" />} Save Changes
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-edit-user-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}

export function ChangeRoleModal({ row, onClose, onSubmit, saving, error }) {
  const [role, setRole] = useState(row.role);
  return (
    <OrgModal title="Change Role" subtitle={row.email} onClose={onClose} testId="org-change-role-modal">
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="org-change-role-select" className={inputClass}>
            {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </div>
        {error && <p className={errorClass} data-testid="org-change-role-error">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => onSubmit({ role })} disabled={saving || role === row.role} data-testid="org-change-role-submit-btn" className={saveBtnClass}>
            {saving && <Loader2 size={14} className="animate-spin" />} Save Role
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-change-role-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}

export function ManageAccessModal({ row, onClose, onSubmit, saving, error }) {
  const [form, setForm] = useState({
    nova_billing_access: !!row.novaBilling,
    catalog_manage: row.role === "team_lead" ? false : !!row.catalogManage,
  });
  const catalogLocked = row.role === "org_admin";
  const catalogBlocked = row.role === "team_lead";
  return (
    <OrgModal title="Manage Access" subtitle={row.email} onClose={onClose} testId="org-manage-access-modal">
      <div className="space-y-3.5">
        <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={form.nova_billing_access} onChange={(e) => setForm((f) => ({ ...f, nova_billing_access: e.target.checked }))} data-testid="org-manage-access-nova-billing" />
          Nova Billing Access
        </label>
        <label className={`flex items-center gap-2.5 text-sm font-semibold ${catalogLocked || catalogBlocked ? "text-slate-400" : "text-slate-700 dark:text-slate-300"}`}>
          <input
            type="checkbox"
            checked={catalogLocked ? true : catalogBlocked ? false : form.catalog_manage}
            disabled={catalogLocked || catalogBlocked}
            onChange={(e) => setForm((f) => ({ ...f, catalog_manage: e.target.checked }))}
            data-testid="org-manage-access-catalog"
          />
          Catalog Image Access {catalogLocked && "(included with Organization Admin)"}{catalogBlocked && "(not available for Team Leads)"}
        </label>
        {error && <p className={errorClass} data-testid="org-manage-access-error">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => onSubmit(form)} disabled={saving} data-testid="org-manage-access-submit-btn" className={saveBtnClass}>
            {saving && <Loader2 size={14} className="animate-spin" />} Save Access
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-manage-access-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}

export function TransferOwnershipModal({ row, candidates, onClose, onSubmit, saving, error }) {
  const [newOwnerId, setNewOwnerId] = useState(candidates[0]?.userId || "");
  return (
    <OrgModal title="Transfer Ownership" subtitle={`Move ownership away from ${row.name || row.email}`} onClose={onClose} testId="org-transfer-ownership-modal">
      <div className="space-y-4">
        {candidates.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-white/15 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-transfer-no-candidates">
            No other active organization user is eligible to become the new owner yet. Invite or activate a user first.
          </p>
        ) : (
          <div>
            <label className={labelClass}>New Owner</label>
            <select value={newOwnerId} onChange={(e) => setNewOwnerId(e.target.value)} data-testid="org-transfer-select" className={inputClass}>
              {candidates.map((c) => <option key={c.userId} value={c.userId}>{c.name || c.email} ({c.email})</option>)}
            </select>
          </div>
        )}
        <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="org-transfer-warning">
          {row.name || row.email} will be moved to Organization Admin automatically. This organization will always keep exactly one Owner.
        </p>
        {error && <p className={errorClass} data-testid="org-transfer-error">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="button" onClick={() => onSubmit({ newOwnerId })} disabled={saving || !newOwnerId} data-testid="org-transfer-submit-btn" className={saveBtnClass}>
            {saving && <Loader2 size={14} className="animate-spin" />} Transfer Ownership
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-transfer-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}

export function ViewProfileModal({ row, onClose }) {
  return (
    <OrgModal title={row.name || row.email} subtitle="Organization user profile" onClose={onClose} testId="org-view-profile-modal">
      <div className="grid grid-cols-2 gap-4" data-testid="org-view-profile-fields">
        <Field label="Name" value={row.name} testId="org-view-profile-name" />
        <Field label="Email" value={row.email} testId="org-view-profile-email" />
        <Field label="Role" value={ROLE_LABELS[row.role] || row.role} testId="org-view-profile-role" />
        <Field label="Status" value={row.status} testId="org-view-profile-status" />
        <Field label="Last Active" value={row.lastActive ? formatDateTime(row.lastActive) : "—"} testId="org-view-profile-last-active" />
        {row.kind === "user" && <Field label="Nova Billing Access" value={row.novaBilling ? "Yes" : "No"} testId="org-view-profile-nova-billing" />}
      </div>
    </OrgModal>
  );
}

export function ConfirmActionModal({ title, message, confirmLabel, danger, onClose, onConfirm, saving, error }) {
  return (
    <OrgModal title={title} onClose={onClose} testId="org-confirm-action-modal">
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="org-confirm-action-message">{message}</p>
        {error && <p className={errorClass} data-testid="org-confirm-action-error">{error}</p>}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            data-testid="org-confirm-action-btn"
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {saving && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
          </button>
          <button type="button" onClick={onClose} disabled={saving} data-testid="org-confirm-action-cancel-btn" className={cancelBtnClass}>Cancel</button>
        </div>
      </div>
    </OrgModal>
  );
}
